import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Input, Modal, Alert, Icon, DropdownSelect, DatePicker, MacTitlebar, toast, type DropdownOption } from '@/components/ui';
import { listClients } from '@/lib/clients';
import { getCompany, useCompanyLogoUrl, uploadCompanyLogo, deleteCompanyLogo } from '@/lib/company';
import { createInvoice, sendInvoiceEmail, exportInvoice, downloadBlob, getNextInvoiceNumber, getInvoice, patchInvoice } from '@/lib/invoices';
import { InvoicePreview } from '@/components/invoice/InvoicePreview';
import { THEMES, ACCENT_SWATCHES } from '@/lib/themes';
import type { LineItem, Party, Invoice } from '@/types/invoice';

const emptyParty = (): Party => ({ name: '', address: '', taxId: '', email: '', phone: '' });
const emptyLine  = (): LineItem => ({ description: '', quantity: 1, unitPrice: 0, taxRate: 0, discountBp: 0, total: 0 });

/** Pretty-print a dollar amount with the right currency symbol. */
function fmt(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: currency || 'MAD' }).format(amount);
  } catch {
    return `${amount.toFixed(2)} ${currency || 'MAD'}`;
  }
}

const CURRENCY_OPTIONS: DropdownOption[] = [
  { value: 'MAD', label: 'MAD (Dirham Marocain)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'GBP', label: 'GBP (£)' },
  { value: 'CAD', label: 'CAD ($)' },
  { value: 'CHF', label: 'CHF' },
  { value: 'AUD', label: 'AUD ($)' },
  { value: 'JPY', label: 'JPY (¥)' },
  { value: 'CNY', label: 'CNY (¥)' },
];

export function CreatePage() {
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('id');
  const isEditing = !!editId;
  const today = new Date().toISOString().slice(0, 10);

  // Pull defaults from the user's saved company profile.
  const companyQ = useQuery({ queryKey: ['company'], queryFn: getCompany });
  const clientsQ = useQuery({ queryKey: ['clients', { q: '' }], queryFn: () => listClients({ pageSize: 100 }) });
  const nextNumQ = useQuery({
    queryKey: ['invoice-next-number'],
    queryFn: getNextInvoiceNumber,
    enabled: !isEditing,
  });
  const existingQ = useQuery({
    queryKey: ['invoice', editId],
    queryFn: () => getInvoice(editId as string),
    enabled: isEditing,
  });

  // Form state — dollars in the UI, converted to cents on submit.
  const [vendor, setVendor] = useState<Party>(emptyParty());
  const [buyer,  setBuyer]  = useState<Party>(emptyParty());
  const [clientId, setClientId] = useState<string | null>(null);
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [issueDate, setIssueDate] = useState(today);
  const [dueDate,   setDueDate]   = useState('');
  const [currency,  setCurrency]  = useState('MAD');
  const [lineItems, setLineItems] = useState<LineItem[]>([emptyLine()]);
  const [paymentTerms, setPaymentTerms] = useState('');
  const [notes,        setNotes]        = useState('');
  const [taxPercent, setTaxPercent] = useState(0);

  const [theme, setTheme]       = useState<string>('minimal');
  const [accentColor, setAccent] = useState<string>('#ed5c25');

  const [sendOpen, setSendOpen] = useState(false);
  const [savedInvoice, setSavedInvoice] = useState<Invoice | null>(null);
  const [logoCacheBust, setLogoCacheBust] = useState(() => String(Date.now()));
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [mobileTab, setMobileTab] = useState<'form' | 'preview'>('form');

  // Preview pane controls: zoom (% of A4 natural width), pane width override,
  // pointer-drag pan, ctrl/wheel zoom anchored on the cursor. State is local;
  // the underlying mechanics mirror the invoice detail preview.
  const ZOOM_MIN = 0.3;
  const ZOOM_MAX = 2.5;
  const ZOOM_STEP = 0.1;
  const PANE_MIN = 360;
  const PANE_MAX = 1200;
  const [previewZoom, setPreviewZoom] = useState<number>(() => {
    try {
      const stored = Number(window.localStorage.getItem('mi.create.previewZoom'));
      if (Number.isFinite(stored) && stored >= ZOOM_MIN && stored <= ZOOM_MAX) return stored;
    } catch { /* ignore */ }
    return 0.55;
  });
  const [paneWidth, setPaneWidth] = useState<number | null>(() => {
    try {
      const stored = Number(window.localStorage.getItem('mi.create.previewWidth'));
      if (Number.isFinite(stored) && stored >= PANE_MIN && stored <= PANE_MAX) return stored;
    } catch { /* ignore */ }
    return null;
  });
  useEffect(() => {
    try { window.localStorage.setItem('mi.create.previewZoom', String(previewZoom)); }
    catch { /* ignore */ }
  }, [previewZoom]);
  useEffect(() => {
    if (paneWidth === null) return;
    try { window.localStorage.setItem('mi.create.previewWidth', String(paneWidth)); }
    catch { /* ignore */ }
  }, [paneWidth]);
  const previewScrollRef = useRef<HTMLDivElement | null>(null);
  const previewPaneRef = useRef<HTMLElement | null>(null);
  const zoomRef = useRef(previewZoom);
  useEffect(() => { zoomRef.current = previewZoom; }, [previewZoom]);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);

  // Apply pane width as a grid-template-columns override on the parent grid.
  useEffect(() => {
    const grid = previewPaneRef.current?.parentElement;
    if (!grid) return;
    if (paneWidth === null) grid.style.removeProperty('grid-template-columns');
    else grid.style.gridTemplateColumns = `minmax(0, 1fr) ${paneWidth}px`;
  }, [paneWidth]);

  // Ctrl/⌘+wheel zooms (anchored on the cursor) + plain pointer-down pans.
  useEffect(() => {
    const el = previewScrollRef.current;
    if (!el) return undefined;

    function onWheel(e: WheelEvent) {
      if (!(e.ctrlKey || e.metaKey) || !el) return;
      e.preventDefault();
      const prev = zoomRef.current;
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX,
        +(prev + (e.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP)).toFixed(2)));
      if (next === prev) return;
      const rect = el.getBoundingClientRect();
      const cursorX = e.clientX - rect.left + el.scrollLeft;
      const cursorY = e.clientY - rect.top + el.scrollTop;
      const ratio = next / prev;
      setPreviewZoom(next);
      requestAnimationFrame(() => {
        if (!el) return;
        el.scrollLeft = cursorX * ratio - (e.clientX - rect.left);
        el.scrollTop  = cursorY * ratio - (e.clientY - rect.top);
      });
    }

    let panStart: { x: number; y: number; left: number; top: number; id: number } | null = null;
    function onPointerDown(e: PointerEvent) {
      if (!el || e.button !== 0 || e.ctrlKey || e.metaKey || e.shiftKey || e.altKey) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'A' || tag === 'BUTTON' || tag === 'INPUT' || tag === 'TEXTAREA') return;
      panStart = { x: e.clientX, y: e.clientY, left: el.scrollLeft, top: el.scrollTop, id: e.pointerId };
      el.setPointerCapture(e.pointerId);
      el.classList.add('panning');
    }
    function onPointerMove(e: PointerEvent) {
      if (!panStart || !el) return;
      el.scrollLeft = panStart.left - (e.clientX - panStart.x);
      el.scrollTop  = panStart.top  - (e.clientY - panStart.y);
    }
    function onPointerUp(e: PointerEvent) {
      if (!panStart || !el) return;
      try { el.releasePointerCapture(panStart.id); } catch { /* ignore */ }
      panStart = null;
      el.classList.remove('panning');
      void e;
    }

    el.addEventListener('wheel', onWheel, { passive: false });
    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', onPointerUp);
    el.addEventListener('pointercancel', onPointerUp);
    return () => {
      el.removeEventListener('wheel', onWheel);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerUp);
    };
  }, []);

  const zoomIn   = () => setPreviewZoom((z) => Math.min(ZOOM_MAX, +(z + ZOOM_STEP).toFixed(2)));
  const zoomOut  = () => setPreviewZoom((z) => Math.max(ZOOM_MIN, +(z - ZOOM_STEP).toFixed(2)));
  const zoomReset = () => setPreviewZoom(0.55);

  const onPaneResizeStart = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const pane = previewPaneRef.current;
    if (!pane) return;
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
    dragRef.current = { startX: e.clientX, startW: pane.getBoundingClientRect().width };
    document.body.classList.add('is-resizing-ew');
  }, []);
  const onPaneResizeMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const s = dragRef.current;
    if (!s) return;
    const next = Math.max(PANE_MIN, Math.min(PANE_MAX, s.startW - (e.clientX - s.startX)));
    setPaneWidth(next);
  }, []);
  const onPaneResizeEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = null;
    document.body.classList.remove('is-resizing-ew');
    try { (e.target as HTMLElement).releasePointerCapture(e.pointerId); } catch { /* ignore */ }
  }, []);

  // Auto-fill vendor from company defaults on mount (only when not editing).
  useEffect(() => {
    if (isEditing) return;
    const c = companyQ.data;
    if (!c) return;
    setVendor((v) => v.name ? v : {
      name: c.companyName || c.defaultVendor.name || '',
      address: c.defaultVendor.address || '',
      taxId: c.defaultVendor.taxId || '',
      email: c.defaultVendor.email || '',
      phone: c.defaultVendor.phone || '',
    });
  }, [companyQ.data, isEditing]);

  // Pull the next sequential number once it's loaded.
  useEffect(() => {
    if (nextNumQ.data && !invoiceNumber) setInvoiceNumber(nextNumQ.data);
  }, [nextNumQ.data, invoiceNumber]);

  // Edit mode: hydrate every field from the saved invoice once.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!isEditing || hydratedRef.current) return;
    const inv = existingQ.data;
    if (!inv) return;
    hydratedRef.current = true;
    setVendor(inv.vendor);
    setBuyer(inv.buyer);
    setClientId(inv.clientId);
    setInvoiceNumber(inv.invoiceNumber);
    setIssueDate(inv.issueDate?.slice(0, 10) ?? '');
    setDueDate(inv.dueDate?.slice(0, 10) ?? '');
    setCurrency(inv.currency || 'MAD');
    // Convert cents back to dollars for the form's working units.
    setLineItems(inv.lineItems.map((li) => ({
      description: li.description,
      quantity: li.quantity,
      unitPrice: (li.unitPrice || 0) / 100,
      taxRate: li.taxRate || 0,
      discountBp: li.discountBp ?? 0,
      total: (li.total || 0) / 100,
    })));
    setPaymentTerms(inv.paymentTerms ?? '');
    setNotes(inv.notes ?? '');
    // Tax percent: best-effort from invoice (taxTotal / subtotal * 100).
    if (inv.subtotal > 0) setTaxPercent(Math.round((inv.taxTotal / inv.subtotal) * 100));
    setTheme(inv.theme || 'minimal');
    setAccent(inv.accentColor || '#ed5c25');
    setSavedInvoice(inv);
  }, [isEditing, existingQ.data]);

  // Recalculate per-line totals + invoice totals as the user types.
  // Line total = qty * unit * (1 - discount/100)
  const { subtotalCents, taxTotalCents, totalCents } = useMemo(() => {
    let sub = 0;
    for (const li of lineItems) {
      const gross = (li.quantity || 0) * (li.unitPrice || 0);
      const off = ((li.discountBp ?? 0) / 10000); // basis points -> ratio
      const net = gross * (1 - off);
      sub += Math.round(net * 100);
    }
    const tax = Math.round(sub * (taxPercent / 100));
    return { subtotalCents: sub, taxTotalCents: tax, totalCents: sub + tax };
  }, [lineItems, taxPercent]);

  // Build the in-memory preview object — dollars on screen, but the preview
  // component expects cents, so we mirror the wire format.
  const previewInvoice = useMemo(() => ({
    vendor, buyer,
    invoiceNumber,
    issueDate, dueDate: dueDate || null,
    currency,
    lineItems: lineItems.map((li) => {
      const gross = (li.quantity || 0) * (li.unitPrice || 0);
      const net = gross * (1 - (li.discountBp ?? 0) / 10000);
      return {
        ...li,
        unitPrice: Math.round((li.unitPrice || 0) * 100),
        total: Math.round(net * 100),
        taxRate: Math.round((taxPercent || 0) * 100),
        discountBp: li.discountBp ?? 0,
      };
    }),
    subtotal: subtotalCents,
    taxTotal: taxTotalCents,
    total: totalCents,
    paymentTerms, notes,
    theme, accentColor,
  }), [vendor, buyer, invoiceNumber, issueDate, dueDate, currency, lineItems, paymentTerms, notes, theme, accentColor, taxPercent, subtotalCents, taxTotalCents, totalCents]);

  const logoUrl = useCompanyLogoUrl(companyQ.data?.hasLogo, logoCacheBust);

  function setLine(idx: number, patch: Partial<LineItem>) {
    setLineItems((rows) => rows.map((r, i) => i === idx ? { ...r, ...patch } : r));
  }
  function addLine() { setLineItems((rows) => [...rows, emptyLine()]); }
  function removeLine(idx: number) {
    setLineItems((rows) => rows.length <= 1 ? rows : rows.filter((_, i) => i !== idx));
  }
  function applyClient(id: string | null) {
    setClientId(id);
    if (!id) return;
    const c = clientsQ.data?.items.find((x) => x.id === id);
    if (!c) return;
    setBuyer({
      name: c.name || c.companyName,
      address: c.address,
      taxId: c.taxId,
      email: c.email,
      phone: c.phone,
    });
  }
  function resetForm() {
    setBuyer(emptyParty());
    setClientId(null);
    setLineItems([emptyLine()]);
    setPaymentTerms('');
    setNotes('');
    setTaxPercent(0);
    setCurrency('MAD');
    setSubmitAttempted(false);
    setSavedInvoice(null);
  }

  // Inline logo upload, shared with the Settings → Company section.
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadLogoMut = useMutation({
    mutationFn: (file: File) => uploadCompanyLogo(file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company'] });
      setLogoCacheBust(String(Date.now()));
      toast('Logo mis à jour.', 'success');
    },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Échec du téléversement', 'danger'),
  });
  const removeLogoMut = useMutation({
    mutationFn: () => deleteCompanyLogo(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['company'] });
      toast('Logo supprimé.', 'info');
    },
  });
  function onLogoFile(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (f) uploadLogoMut.mutate(f);
    e.target.value = '';
  }

  // Validation: must have a vendor, an invoice number, at least one priced line item.
  const validation = useMemo(() => {
    const errors: { field: string; msg: string }[] = [];
    if (!vendor.name.trim())  errors.push({ field: 'vendor.name', msg: 'Renseignez le nom de votre entreprise dans le bloc Émetteur (ou dans les Paramètres).' });
    if (!invoiceNumber.trim()) errors.push({ field: 'invoiceNumber', msg: 'Le numéro de facture est obligatoire.' });
    const priced = lineItems.filter((li) => (li.description.trim() && (li.quantity || 0) > 0 && (li.unitPrice || 0) > 0));
    if (priced.length === 0) errors.push({ field: 'lineItems', msg: 'Ajoutez au moins une ligne avec description, quantité et prix unitaire.' });
    return errors;
  }, [vendor.name, invoiceNumber, lineItems]);
  const isValid = validation.length === 0;

  const save = useMutation({
    mutationFn: (opts: { draft: boolean }) => {
      const payload = {
        vendor, buyer,
        invoiceNumber,
        issueDate, dueDate: dueDate || null,
        currency,
        lineItems: previewInvoice.lineItems,
        subtotal: subtotalCents, taxTotal: taxTotalCents, total: totalCents,
        paymentTerms: paymentTerms || null,
        notes: notes || null,
        theme, accentColor,
        clientId: clientId || null,
      };
      if (isEditing && editId) {
        // Existing invoice — PATCH and keep the same id.
        return patchInvoice(editId, payload);
      }
      return createInvoice({ ...payload, draft: opts.draft });
    },
    onSuccess: (inv) => {
      setSavedInvoice(inv);
      void qc.invalidateQueries({ queryKey: ['invoices'] });
      void qc.invalidateQueries({ queryKey: ['invoice-next-number'] });
      if (isEditing) void qc.invalidateQueries({ queryKey: ['invoice', editId] });
      toast(isEditing ? 'Modifications enregistrées.' : 'Facture enregistrée.', 'success');
    },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Échec de l\'enregistrement', 'danger'),
  });

  async function handleSaveOnly() {
    setSubmitAttempted(true);
    if (!isValid) { toast('Veuillez corriger les anomalies signalées ci-dessous.', 'danger'); return; }
    await save.mutateAsync({ draft: false });
  }
  async function handleDownload() {
    setSubmitAttempted(true);
    if (!isValid) { toast('Veuillez corriger les anomalies signalées ci-dessous.', 'danger'); return; }
    if (!savedInvoice) await save.mutateAsync({ draft: false });
    const inv = save.data ?? savedInvoice;
    if (!inv) return;
    try {
      const blob = await exportInvoice(inv.id, 'pdf');
      downloadBlob(blob, `${invoiceNumber || 'facture'}.pdf`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Échec de l\'export PDF', 'danger');
    }
  }
  async function handleSaveAndSend() {
    setSubmitAttempted(true);
    if (!isValid) { toast('Veuillez corriger les anomalies signalées ci-dessous.', 'danger'); return; }
    if (!savedInvoice) await save.mutateAsync({ draft: false });
    setSendOpen(true);
  }

  const clientOptions: DropdownOption[] = [
    { value: '', label: 'Aucun client (saisie manuelle)' },
    ...(clientsQ.data?.items ?? []).map<DropdownOption>((c) => ({
      value: c.id,
      label: c.companyName || c.name || c.email,
      hint: c.email || c.phone || undefined,
    })),
  ];

  return (
    <div className="create-page">
      <div className="create-page-header">
        <div className="row-between">
          <div>
            <h1 style={{ margin: 0, fontSize: 28, letterSpacing: '-0.025em' }}>
              {isEditing ? 'Modifier la facture' : 'Créer une facture'}
            </h1>
            <p className="muted" style={{ margin: '4px 0 0' }}>
              {isEditing
                ? 'Mettez à jour les champs, changez le thème, visualisez en direct puis enregistrez vos modifications.'
                : 'Choisissez un thème, remplissez les détails, visualisez en direct puis enregistrez, téléchargez ou envoyez.'}
            </p>
          </div>
          <div className="row-gap">
            <Button variant="ghost" size="sm" onClick={resetForm} title="Effacer tous les champs">
              Réinitialiser
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleSaveOnly}
              disabled={save.isPending}
              leadingIcon={<Icon name="check" size={14} />}
              title={isEditing ? 'Enregistrer les modifications' : 'Enregistrer la facture'}
            >
              {save.isPending ? 'Enregistrement…' : isEditing ? 'Enregistrer les modifications' : 'Enregistrer'}
            </Button>
            <Button variant="secondary" size="sm" onClick={handleDownload} disabled={save.isPending} leadingIcon={<Icon name="download" size={14} />}>
              Enregistrer & télécharger
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveAndSend}
              disabled={save.isPending}
              leadingIcon={<Icon name="mail" size={14} />}
            >
              Enregistrer & envoyer
            </Button>
          </div>
        </div>

        {submitAttempted && !isValid && (
          <div style={{ marginTop: 12 }}>
            <Alert tone="warning" title="Certains champs nécessitent votre attention">
              <ul style={{ margin: '4px 0 0 18px', padding: 0 }}>
                {validation.map((e) => <li key={e.field}>{e.msg}</li>)}
              </ul>
            </Alert>
          </div>
        )}

        {/* Switcher mobile pour basculer facilement entre formulaire et aperçu direct */}
        <div className="create-mobile-nav">
          <button
            type="button"
            className={`create-nav-tab ${mobileTab === 'form' ? 'active' : ''}`}
            onClick={() => setMobileTab('form')}
          >
            <Icon name="invoices" size={14} /> Formulaire & Saisie
          </button>
          <button
            type="button"
            className={`create-nav-tab ${mobileTab === 'preview' ? 'active' : ''}`}
            onClick={() => setMobileTab('preview')}
          >
            <Icon name="eye" size={14} /> Aperçu direct
          </button>
        </div>
      </div>

      <div className={`create-grid ${mobileTab === 'preview' ? 'show-preview-mobile' : 'show-form-mobile'}`}>
        {/* LEFT — form (ONLY this column scrolls!) */}
        <div className="create-form-column stack-lg">
          <Card>
            <h3 className="card-section-title">Logo de l'entreprise</h3>
            <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
              {companyQ.data?.hasLogo && logoUrl ? (
                <img
                  src={logoUrl}
                  alt="Logo de l'entreprise"
                  style={{ width: 132, height: 70, objectFit: 'contain', background: 'var(--surface-3)', borderRadius: 8, padding: 8 }}
                />
              ) : (
                <div style={{ width: 132, height: 70, background: 'var(--surface-3)', borderRadius: 8, display: 'grid', placeItems: 'center', color: 'var(--ink-4)', fontSize: 11, padding: 8 }}>
                  Aucun logo pour l'instant
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/svg+xml"
                  hidden
                  onChange={onLogoFile}
                />
                <div className="row-gap">
                  <Button variant="secondary" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadLogoMut.isPending} leadingIcon={<Icon name="upload" size={14} />}>
                    {uploadLogoMut.isPending ? 'Téléversement…' : companyQ.data?.hasLogo ? 'Remplacer le logo' : 'Téléverser un logo'}
                  </Button>
                  {companyQ.data?.hasLogo && (
                    <Button variant="ghost" size="sm" onClick={() => removeLogoMut.mutate()} disabled={removeLogoMut.isPending}>
                      Supprimer
                    </Button>
                  )}
                </div>
                <p className="muted" style={{ fontSize: 12, margin: 0 }}>
                  PNG, JPG, WebP ou SVG. Jusqu'à 2 Mo. Également modifiable depuis <Link to="/dashboard/settings">Paramètres → Profil de l'entreprise</Link>.
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="card-section-title">Thème</h3>
            <div className="theme-picker">
              {THEMES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`theme-swatch${theme === t.id ? ' active' : ''}`}
                  onClick={() => { setTheme(t.id); setAccent(t.accent); }}
                  title={t.description}
                >
                  <span className="theme-swatch-band" style={{ background: t.id === theme ? accentColor : t.accent }} />
                  <span className="theme-swatch-body">
                    <strong>{t.label}</strong>
                    <small>{t.description}</small>
                  </span>
                </button>
              ))}
            </div>
            <div className="form-grid" style={{ marginTop: 16 }}>
              <div>
                <label className="field-label">Couleur d'accent</label>
                <div className="accent-row">
                  {ACCENT_SWATCHES.map((c) => (
                    <button
                      key={c}
                      type="button"
                      aria-label={`Couleur d'accent ${c}`}
                      className={`accent-dot${accentColor.toLowerCase() === c.toLowerCase() ? ' active' : ''}`}
                      style={{ background: c }}
                      onClick={() => setAccent(c)}
                    />
                  ))}
                  <input
                    type="color"
                    value={accentColor}
                    onChange={(e) => setAccent(e.target.value)}
                    className="accent-picker"
                    title="Couleur personnalisée"
                  />
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="card-section-title">Informations de la facture</h3>
            <div className="form-grid">
              <Input label="Numéro de facture" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} />
              <DatePicker label="Date d'émission" value={issueDate} onChange={setIssueDate} />
              <DatePicker label="Date d'échéance" value={dueDate} onChange={setDueDate} />
              <DropdownSelect label="Devise" value={currency} onChange={setCurrency} options={CURRENCY_OPTIONS} />
            </div>
          </Card>

          <Card>
            <h3 className="card-section-title">Émetteur (Votre entreprise)</h3>
            <div className="form-grid">
              <Input label="Nom ou Raison sociale" value={vendor.name}    onChange={(e) => setVendor({ ...vendor, name: e.target.value })} />
              <Input label="E-mail" type="email" value={vendor.email} onChange={(e) => setVendor({ ...vendor, email: e.target.value })} />
              <Input label="Téléphone" value={vendor.phone}  onChange={(e) => setVendor({ ...vendor, phone: e.target.value })} />
              <Input label="N° TVA / SIRET" value={vendor.taxId} onChange={(e) => setVendor({ ...vendor, taxId: e.target.value })} />
              <div className="form-grid full" style={{ gridColumn: '1 / -1' }}>
                <Input label="Adresse" value={vendor.address} onChange={(e) => setVendor({ ...vendor, address: e.target.value })} />
              </div>
            </div>
            <p className="muted" style={{ fontSize: 12, marginTop: 8 }}>
              Ces coordonnées sont pré-remplies d'après vos <Link to="/dashboard/settings">Paramètres → Profil de l'entreprise</Link>.
            </p>
          </Card>

          <Card>
            <h3 className="card-section-title">Facturé à (Client)</h3>
            <div className="form-grid">
              <div style={{ gridColumn: '1 / -1' }}>
                <DropdownSelect
                  label="Choisir parmi vos clients enregistrés"
                  value={clientId ?? ''}
                  onChange={(v) => applyClient(v || null)}
                  options={clientOptions}
                />
              </div>
              <Input label="Nom ou Entreprise" value={buyer.name}    onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} />
              <Input label="E-mail" type="email" value={buyer.email} onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} />
              <Input label="Téléphone" value={buyer.phone}   onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} />
              <Input label="N° TVA / SIRET" value={buyer.taxId}   onChange={(e) => setBuyer({ ...buyer, taxId: e.target.value })} />
              <div style={{ gridColumn: '1 / -1' }}>
                <Input label="Adresse" value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} />
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="card-section-title">Articles / Prestations</h3>
            <div className="table-responsive">
              <table className="line-items-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th style={{ width: 70 }}>Qté</th>
                  <th style={{ width: 110 }}>Prix unitaire</th>
                  <th style={{ width: 80 }}>Remise %</th>
                  <th style={{ width: 120 }} className="right">Montant</th>
                  <th style={{ width: 30 }} />
                </tr>
              </thead>
              <tbody>
                {lineItems.map((li, idx) => {
                  const gross = (li.quantity || 0) * (li.unitPrice || 0);
                  const net = gross * (1 - (li.discountBp ?? 0) / 10000);
                  const isLast = idx === lineItems.length - 1;
                  return (
                    <tr key={idx}>
                      <td>
                        <input
                          className="input"
                          value={li.description}
                          placeholder="Description de la prestation ou du produit…"
                          onChange={(e) => setLine(idx, { description: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && isLast && li.description.trim()) {
                              e.preventDefault();
                              addLine();
                            }
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          step={1}
                          min={1}
                          inputMode="numeric"
                          value={li.quantity}
                          onChange={(e) => {
                            const raw = e.target.value;
                            const n = raw === '' ? 0 : Math.max(0, Math.floor(Number(raw)));
                            setLine(idx, { quantity: Number.isFinite(n) ? n : 0 });
                          }}
                          onBlur={(e) => {
                            const n = Math.max(1, Math.floor(Number(e.target.value) || 1));
                            setLine(idx, { quantity: n });
                          }}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          step="0.01"
                          min={0}
                          value={li.unitPrice}
                          onChange={(e) => setLine(idx, { unitPrice: Math.max(0, Number(e.target.value) || 0) })}
                        />
                      </td>
                      <td>
                        <input
                          className="input"
                          type="number"
                          step="0.5"
                          min={0}
                          max={100}
                          value={(li.discountBp ?? 0) / 100}
                          onChange={(e) => {
                            const pct = Math.max(0, Math.min(100, Number(e.target.value) || 0));
                            setLine(idx, { discountBp: Math.round(pct * 100) });
                          }}
                        />
                      </td>
                      <td className="amount right">
                        <input className="input" type="text" value={fmt(net, currency)} readOnly tabIndex={-1} />
                      </td>
                      <td>
                        <button type="button" className="row-action" aria-label="Supprimer la ligne" onClick={() => removeLine(idx)}>×</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
            <Button type="button" size="sm" variant="ghost" onClick={addLine}>
              + Ajouter une ligne
            </Button>
            <div className="form-grid" style={{ marginTop: 16 }}>
              <Input
                label="TVA %"
                type="number"
                step="0.01"
                min={0}
                max={100}
                value={taxPercent}
                onChange={(e) => setTaxPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
              />
              <div>
                <label className="field-label">Totaux (calcul automatique)</label>
                <div className="create-totals">
                  <div className="row"><span>Sous-total HT</span><span className="mono">{fmt(subtotalCents / 100, currency)}</span></div>
                  <div className="row"><span>TVA ({taxPercent}%)</span><span className="mono">{fmt(taxTotalCents / 100, currency)}</span></div>
                  <div className="row total"><span>Total TTC</span><span className="mono">{fmt(totalCents / 100, currency)}</span></div>
                </div>
              </div>
            </div>
          </Card>

          <Card>
            <h3 className="card-section-title">Conditions de règlement & notes</h3>
            <div className="form-grid">
              <Input label="Conditions de règlement" placeholder="À réception / 30 jours net / …" value={paymentTerms} onChange={(e) => setPaymentTerms(e.target.value)} />
              <Input label="Notes" placeholder="Merci de votre confiance" value={notes} onChange={(e) => setNotes(e.target.value)} />
            </div>
          </Card>
        </div>

        {/* RIGHT — live preview */}
        <aside className="create-preview" ref={previewPaneRef as React.RefObject<HTMLElement>}>
          <div
            className="preview-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-label="Redimensionner l'aperçu"
            onPointerDown={onPaneResizeStart}
            onPointerMove={onPaneResizeMove}
            onPointerUp={onPaneResizeEnd}
            onPointerCancel={onPaneResizeEnd}
            onDoubleClick={() => setPaneWidth(null)}
            title="Glisser pour redimensionner · double-clic pour réinitialiser"
          />
          <MacTitlebar title={`${invoiceNumber || 'facture'}.pdf`} />
          <div className="preview-toolbar">
            <button type="button" className="zoom-btn" aria-label="Zoom arrière" onClick={zoomOut} disabled={previewZoom <= ZOOM_MIN}>−</button>
            <button type="button" className="zoom-level" onClick={zoomReset} title="Réinitialiser le zoom">{Math.round(previewZoom * 100)}%</button>
            <button type="button" className="zoom-btn" aria-label="Zoom avant" onClick={zoomIn} disabled={previewZoom >= ZOOM_MAX}>+</button>
            <span className="zoom-hint">Ctrl + molette pour zoomer · glisser pour déplacer</span>
          </div>
          <div className="preview-scroll" ref={previewScrollRef}>
            <InvoicePreview invoice={previewInvoice} logoUrl={logoUrl} scale={previewZoom} />
          </div>
        </aside>
      </div>

      <SendModal
        open={sendOpen}
        onClose={() => setSendOpen(false)}
        invoice={save.data ?? savedInvoice}
        defaultEmail={buyer.email}
        defaultPhone={buyer.phone}
      />
    </div>
  );
}

function SendModal({ open, onClose, invoice, defaultEmail, defaultPhone }:
  { open: boolean; onClose: () => void; invoice: Invoice | null | undefined; defaultEmail: string; defaultPhone: string }
) {
  const [email, setEmail] = useState(defaultEmail);
  const [subject, setSubject] = useState(invoice ? `Facture ${invoice.invoiceNumber || ''}` : 'Votre facture');
  const [message, setMessage] = useState('');
  const [phone, setPhone] = useState(defaultPhone);

  useEffect(() => { setEmail(defaultEmail); }, [defaultEmail]);
  useEffect(() => { setPhone(defaultPhone); }, [defaultPhone]);
  useEffect(() => { if (invoice) setSubject(`Facture ${invoice.invoiceNumber || invoice.id}`); }, [invoice]);

  const sendEmail = useMutation({
    mutationFn: () => sendInvoiceEmail(invoice!.id, { to: email, subject, message }),
    onSuccess: () => { toast(`Facture envoyée à ${email}.`, 'success'); onClose(); },
    onError: (e: unknown) => toast(e instanceof Error ? e.message : 'Échec de l\'envoi', 'danger'),
  });

  function openWhatsApp() {
    if (!phone) { toast('Ajoutez d\'abord un numéro de téléphone.', 'info'); return; }
    const clean = phone.replace(/[^\d+]/g, '');
    const text = `${subject}\n${message || 'Veuillez trouver ci-joint votre facture.'}`;
    window.open(`https://wa.me/${clean.replace(/^\+/, '')}?text=${encodeURIComponent(text)}`, '_blank');
  }
  function openSms() {
    if (!phone) { toast('Ajoutez d\'abord un numéro de téléphone.', 'info'); return; }
    const clean = phone.replace(/[^\d+]/g, '');
    const text = `${subject}\n${message || 'Veuillez trouver ci-joint votre facture.'}`;
    window.open(`sms:${clean}?body=${encodeURIComponent(text)}`, '_self');
  }

  return (
    <Modal
      open={open && !!invoice}
      onClose={onClose}
      title="Envoyer la facture"
      lede="Envoyez la facture par e-mail directement à votre client, ou ouvrez WhatsApp / SMS avec un message pré-rempli."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Fermer</Button>
          <Button variant="primary" disabled={!email || sendEmail.isPending} onClick={() => sendEmail.mutate()}>
            {sendEmail.isPending ? 'Envoi en cours…' : 'Envoyer par e-mail'}
          </Button>
        </>
      }
    >
      {!invoice && <Alert tone="info" title="Enregistrement…">Facture en cours d'enregistrement. Veuillez patienter.</Alert>}
      <div className="stack" style={{ marginTop: 8 }}>
        <Input label="E-mail du destinataire" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        <Input label="Objet" value={subject} onChange={(e) => setSubject(e.target.value)} />
        <Input label="Message (facultatif)" value={message} onChange={(e) => setMessage(e.target.value)} />
        <div style={{ borderTop: '1px solid var(--line)', margin: '8px 0' }} />
        <Input label="Téléphone (pour lien WhatsApp / SMS)" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+33 6 12 34 56 78" />
        <div className="row-gap" style={{ gap: 8 }}>
          <Button variant="secondary" size="sm" onClick={openWhatsApp} leadingIcon={<Icon name="arrow-right" size={14} />}>
            Ouvrir dans WhatsApp
          </Button>
          <Button variant="secondary" size="sm" onClick={openSms} leadingIcon={<Icon name="arrow-right" size={14} />}>
            Ouvrir l'application SMS
          </Button>
        </div>
      </div>
    </Modal>
  );
}
